import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ExternalLink, MapPin } from 'lucide-react';
import { mapsExternalUrl } from '@/types/career';
import { cn } from '@/lib/utils';

interface JobLocationMapProps {
  location: string;
  lat?: number;
  lng?: number;
  className?: string;
  mapClassName?: string;
  showOpenLink?: boolean;
  /** When true, clicking the map places / moves the pin. */
  editable?: boolean;
  onPinChange?: (lat: number, lng: number) => void;
}

const DEFAULT_CENTER: [number, number] = [36.4, 10.6167]; // Hammamet area
const DEFAULT_ZOOM = 13;

/** Inline SVG pin — avoids Leaflet’s default PNG assets (broken under Vite). */
const pinIcon = L.divIcon({
  className: 'job-map-pin',
  html: `<svg width="28" height="40" viewBox="0 0 28 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="#C4A35A"/>
    <circle cx="14" cy="14" r="5.5" fill="#fff"/>
  </svg>`,
  iconSize: [28, 40],
  iconAnchor: [14, 40],
  popupAnchor: [0, -36],
});

const placeMarker = (map: L.Map, latlng: [number, number], existing: L.Marker | null) => {
  if (existing) {
    existing.setLatLng(latlng);
    return existing;
  }
  return L.marker(latlng, { icon: pinIcon }).addTo(map);
};

const JobLocationMap: React.FC<JobLocationMapProps> = ({
  location,
  lat,
  lng,
  className,
  mapClassName,
  showOpenLink = true,
  editable = false,
  onPinChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onPinChangeRef = useRef(onPinChange);
  onPinChangeRef.current = onPinChange;

  const hasPin = Number.isFinite(lat) && Number.isFinite(lng);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const start: [number, number] = hasPin
      ? [lat as number, lng as number]
      : DEFAULT_CENTER;

    const map = L.map(containerRef.current, {
      center: start,
      zoom: hasPin ? 15 : DEFAULT_ZOOM,
      scrollWheelZoom: editable,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    if (hasPin) {
      markerRef.current = placeMarker(map, start, null);
    }

    if (editable) {
      map.on('click', (event: L.LeafletMouseEvent) => {
        const nextLat = event.latlng.lat;
        const nextLng = event.latlng.lng;
        markerRef.current = placeMarker(map, [nextLat, nextLng], markerRef.current);
        onPinChangeRef.current?.(nextLat, nextLng);
      });
      map.getContainer().style.cursor = 'crosshair';
    }

    mapRef.current = map;

    requestAnimationFrame(() => map.invalidateSize());

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // Initialize once; pin updates handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (hasPin) {
      const next: [number, number] = [lat as number, lng as number];
      markerRef.current = placeMarker(map, next, markerRef.current);
      map.setView(next, Math.max(map.getZoom(), 15));
    } else if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }

    requestAnimationFrame(() => map.invalidateSize());
  }, [lat, lng, hasPin]);

  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/70 shadow-soft',
          mapClassName
        )}
      >
        <div ref={containerRef} className="h-56 w-full md:h-72 z-0" />
        {editable ? (
          <div className="pointer-events-none absolute left-3 top-3 z-[500] rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-medium text-foreground shadow">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-furniture-gold" />
              Cliquez sur la carte pour placer le pin
            </span>
          </div>
        ) : null}
        {!editable && !hasPin ? (
          <div className="absolute inset-0 z-[400] flex flex-col items-center justify-center gap-2 bg-muted/80 px-4 text-center text-sm text-muted-foreground">
            <MapPin className="h-5 w-5" />
            <p>Carte non épinglée pour {location || 'cette adresse'}.</p>
          </div>
        ) : null}
      </div>

      {showOpenLink && hasPin ? (
        <a
          href={mapsExternalUrl(lat as number, lng as number, location)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground hover:text-furniture-gold"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Ouvrir dans Google Maps
        </a>
      ) : null}

      <style>{`
        .job-map-pin {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};

export default JobLocationMap;
